import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Search, Filter, Calendar, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const fmt = (v) => `₹${Number(v || 0).toFixed(2)}`;

const STATUS_OPTIONS = ["All", "success", "pending", "failed"];
const GATEWAY_OPTIONS = ["All", "dealer_wallet", "razorpay", "mock", "cash"];

export default function DealerHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [gatewayFilter, setGatewayFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const [searchInput, setSearchInput] = useState("");

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [statusFilter, gatewayFilter, dateFrom, dateTo]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("limit", PAGE_SIZE);
    if (search) params.set("search", search);
    if (statusFilter !== "All") params.set("status", statusFilter);
    if (gatewayFilter !== "All") params.set("gateway", gatewayFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    api.get(`/dealer/recharges?${params.toString()}`)
      .then((r) => {
        setRows(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error("Failed to load history"))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, gatewayFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const totalAmount = useMemo(
    () => rows.reduce((acc, r) => acc + (r.status === "success" ? parseFloat(r.amount || 0) : 0), 0),
    [rows]
  );

  const statusBadge = (status) => {
    const cls = {
      success: "bg-green-50 text-green-700",
      failed: "bg-red-50 text-red-600",
      pending: "bg-amber-50 text-amber-600",
    };
    return (
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls[status] || "bg-slate-100 text-slate-500"}`}>
        {status}
      </span>
    );
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("All");
    setGatewayFilter("All");
    setDateFrom("");
    setDateTo("");
  };

  const hasFilters = search || statusFilter !== "All" || gatewayFilter !== "All" || dateFrom || dateTo;

  if (loading) return <div className="pt-8 text-center text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl text-brand-nav">Recharge History</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {total} transaction{total !== 1 ? "s" : ""}
          </p>
        </div>
        {/* Summary chip */}
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shrink-0">
          <TrendingUp className="w-4 h-4 text-brand-accent" />
          <div>
            <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Total (success)</p>
            <p className="font-heading font-bold text-lg text-brand-nav leading-tight">{fmt(totalAmount)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Filters</span>
          {hasFilters && (
            <button onClick={clearFilters} className="ml-auto text-xs text-brand-primary font-semibold hover:underline">
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-[10px] text-slate-400 font-semibold uppercase mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="ID, gateway..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-8 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-[10px] text-slate-400 font-semibold uppercase mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s === "All" ? "All Statuses" : s}</option>
              ))}
            </select>
          </div>

          {/* Gateway */}
          <div>
            <label className="block text-[10px] text-slate-400 font-semibold uppercase mb-1">Gateway</label>
            <select
              value={gatewayFilter}
              onChange={(e) => setGatewayFilter(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
            >
              {GATEWAY_OPTIONS.map((g) => (
                <option key={g} value={g}>{g === "All" ? "All Gateways" : g}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-[10px] text-slate-400 font-semibold uppercase mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-[10px] text-slate-400 font-semibold uppercase mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">
          <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="font-medium">No recharges match your filters.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Txn No</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Customer / DTH</th>
                  <th className="text-left px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Breakdown</th>
                  <th className="text-left px-4 py-3">Gateway</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-xs font-mono font-semibold text-brand-primary">
                      {r.txn_no || r.id?.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-500">
                      {r.created_at?.slice(0, 10)}
                      <div className="text-[10px] text-slate-400">{r.created_at?.slice(11, 16)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-xs text-brand-nav">{r.dth_id || "—"}</div>
                      <div className="text-[10px] text-slate-500">{r.customer_name || "Unknown"} {r.customer_mobile ? `(${r.customer_mobile})` : ""}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-brand-nav">{fmt(r.amount)}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-[10px] text-slate-500">
                      <div>MRP: {r.amount_mrp ? fmt(r.amount_mrp) : "—"}</div>
                      {(r.addon_charge > 0 || r.extra_discount > 0) && (
                        <div className="mt-0.5">
                          {r.addon_charge > 0 && <span className="text-amber-600">+{fmt(r.addon_charge)}</span>}
                          {r.extra_discount > 0 && <span className="text-green-600 ml-1">-{fmt(r.extra_discount)}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">{r.gateway}</span>
                    </td>
                    <td className="px-4 py-3">{statusBadge(r.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
        </div>
      )}
    </div>
  );
}
